// React and hooks
import { useMsal } from "@azure/msal-react";
import { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

// FluentUI components
import {
    Checkbox,
    DefaultButton,
    IContextualMenuItem,
    IPanelProps,
    IRenderFunction,
    Label,
    Panel,
    PanelType,
    PrimaryButton,
    ProgressIndicator,
    SearchBox,
    Separator,
    Spinner,
    Stack,
    Text,
} from "@fluentui/react";

// External libraries
import { saveAs } from 'file-saver';

// App imports
import { RootState } from "app/store";
import LinkMenu from "components/controls/LinkMenu";
import { emitCustomEvent, panelButtonsStyles } from "global/Application";
import { IPlanComparisonGroupConfigurationData } from "global/ExcelExtract/PlanComparisonReportGeneration";
import { setProcessing } from "global/Reducer/statusSlice";
import { AppDataSource } from "../../../global/DataSources/AppDataSource";
import { MsalTokenSource } from "../../../global/DataSources/MsalTokenSource";
import {
    IGroupConfigurationData as FAM_IGroupConfigurationData,
    IPlan as FAM_IPlan
} from "../../../model/FlexibleAttributeModel";

// Local components
import { appInsights } from "global/Application/AppInsight";
import CustomPanel from "../CheckList/CheckListItem/CustomPanel";
import SnapshotLookup from "../Controls/SnapshotLookup";
import PlanCard from "../PlanCard";

// Types and interfaces
interface IPlanComparisonReportProps {
    isOpen: boolean;
    onCancel?: () => void;
    onDismiss: () => void;
}

interface ISelectablePlan {
    plan: FAM_IPlan;
    selected: boolean;
    hidden?: boolean;
}

interface ISnapshotItem {
    id: string | number;
    plans: ISelectablePlan[];
    snapshot: any;
    json: FAM_IGroupConfigurationData;
    search: string | null;
}

interface IViewModel {
    loading: boolean;
    ignoreEmptyFields: boolean;
    showOnlyChanges: boolean;
}

interface ITopBarComponentProps {
    onSearch: (value: string) => void;
    onClear: (event: any) => void;
}

interface IComparisonSettings {
    ignoreEmptyFields: boolean;
    showOnlyChanges: boolean;
}

interface ISettingsPanelProps {
    viewModel: IViewModel;
    onSettingsChange: (updater: (prev: IViewModel) => IViewModel) => void;
}


interface ISnapshotPanelProps {
    snapshotItem: ISnapshotItem;
    index: number;
    state: RootState;
    onTogglePlanSelection: (entry: ISnapshotItem, plan: ISelectablePlan) => void;
    onFilterPlans: (entry: ISnapshotItem, searchTerm: string) => void;
    onRemoveSnapshot: (itemId: string | number) => void;
    onInvertSelection: (index: number) => void;
}

interface IImportSnapshotPanelProps {
    items: ISnapshotItem[];
    viewModel: IViewModel;
    dataSource: AppDataSource;
    onAddSnapshot: (item: ISnapshotItem) => void;
    onSetLoading: (loading: boolean) => void;
}

// Utility functions
const createSnapshotItemFromState = (state: RootState): ISnapshotItem => {
    const snapshotClone = structuredClone(state.data.snapshot);
    const jsonClone = structuredClone(state.data.json);

    const plans = jsonClone.plans.map((plan): ISelectablePlan => ({
        plan,
        selected: false,
    }));

    return {
        id: snapshotClone.bcbs_groupconfigurationsnapshotid!,
        plans,
        snapshot: snapshotClone,
        json: jsonClone,
        search: null,
    };
};

const createSnapshotItemFromSnapshot = (snapshot: any, json: FAM_IGroupConfigurationData): ISnapshotItem => {
    const plans = json.plans.map((plan): ISelectablePlan => ({
        plan,
        selected: false,
    }));

    return {
        id: snapshot.bcbs_groupconfigurationsnapshotid!,
        plans,
        snapshot,
        json,
        search: null,
    };
};

const buildComparisonData = (items: ISnapshotItem[], options: IComparisonSettings): IPlanComparisonGroupConfigurationData[] => {
    const jsons: IPlanComparisonGroupConfigurationData[] = [];

    items.forEach(item => {
        item.plans.forEach(eachPlan => {
            if (!eachPlan.selected) return;

            const plan = eachPlan.plan;
            const json: IPlanComparisonGroupConfigurationData = {
                snapshot: item.snapshot,
                options: options,
                json: {
                    name: item.json.name,
                    plans: [plan],
                    benefits: plan.benefitRefs?.map(benefitRef =>
                        item.json.benefits.find(b => b.id === benefitRef.id)!
                    ),
                    feesAndPremiums: [],
                    groupStructure: {
                        ...item.json.groupStructure,
                        groupNumbers: item.json.groupStructure.groupNumbers.filter(
                            gn => gn.planRef?.id === plan.id
                        )
                    },
                }
            }

            if (plan.feesAndPremiumsRef) {
                const feesAndPremium = item.json.feesAndPremiums.find(
                    fp => fp.id === plan.feesAndPremiumsRef!.id
                )!;
                json.json.feesAndPremiums.push(feesAndPremium);
            }

            jsons.push(json);
        });
    });

    return jsons;
};

// Custom hook for managing snapshot items
const useSnapshotItems = (init: boolean, state: RootState) => {
    const [snapshotItems, setSnapshotItems] = useState<ISnapshotItem[]>([]);

    useEffect(() => {
        if (init && snapshotItems.length === 0) {
            setSnapshotItems([createSnapshotItemFromState(state)]);
        }
    }, [init, snapshotItems.length, state]);

    const togglePlanSelection = useCallback((entry: ISnapshotItem, plan: ISelectablePlan) => {
        setSnapshotItems(prevItems =>
            prevItems.map(item => {
                if (item.id !== entry.id) return item;

                const updatedPlans = item.plans.map(p =>
                    p.plan.id === plan.plan.id
                        ? { ...p, selected: !p.selected }
                        : p
                );
                return { ...item, plans: updatedPlans };
            })
        );
    }, []);

    const filterPlans = useCallback((entry: ISnapshotItem, searchTerm: string) => {
        setSnapshotItems(prevItems =>
            prevItems.map(item => {
                if (item.id !== entry.id) return item;

                const updatedPlans = item.plans.map(p => ({
                    ...p,
                    hidden: !p.selected && !p.plan.name.toLowerCase().includes(searchTerm)
                }));
                return { ...item, plans: updatedPlans };
            })
        );
    }, []);

    const addSnapshotItem = useCallback((item: ISnapshotItem) => {
        setSnapshotItems(prevItems => [...prevItems, item]);
    }, []);

    const removeSnapshotItem = useCallback((itemId: string | number) => {
        setSnapshotItems(prevItems => prevItems.filter(item => item.id !== itemId));
    }, []);

    const invertSelection = useCallback((index: number) => {
        setSnapshotItems(prevItems =>
            prevItems.map((item, i) => {
                if (i !== index) return item;

                const updatedPlans = item.plans.map(p => ({
                    ...p,
                    selected: !p.hidden ? !p.selected : p.selected
                }));
                return { ...item, plans: updatedPlans };
            })
        );
    }, []);

    return {
        snapshotItems,
        togglePlanSelection,
        filterPlans,
        addSnapshotItem,
        removeSnapshotItem,
        invertSelection,
    };
};

// Custom hook for managing snapshot items using useReducer
const useSnapshotItemsReducer = (init: boolean, rootState: RootState) => {

    type Action =
        | { type: 'INIT_FROM_STATE'; payload: RootState }
        | { type: 'TOGGLE_PLAN_SELECTION'; payload: { entryId: string | number; planId: string | number } }
        | { type: 'FILTER_PLANS'; payload: { entryId: string | number; searchTerm: string } }
        | { type: 'ADD_SNAPSHOT_ITEM'; payload: ISnapshotItem }
        | { type: 'REMOVE_SNAPSHOT_ITEM'; payload: { itemId: string | number } }
        | { type: 'INVERT_SELECTION'; payload: { index: number } };

    const reducer = (stateItems: ISnapshotItem[], action: Action): ISnapshotItem[] => {
        switch (action.type) {
            case 'INIT_FROM_STATE': {
                if (stateItems.length > 0) return stateItems;
                return [createSnapshotItemFromState(action.payload)];
            }
            case 'TOGGLE_PLAN_SELECTION': {
                const { entryId, planId } = action.payload;
                return stateItems.map(item => {
                    if (item.id !== entryId) return item;
                    const plans = item.plans.map(p =>
                        p.plan.id === planId ? { ...p, selected: !p.selected } : p
                    );
                    return { ...item, plans };
                });
            }
            case 'FILTER_PLANS': {
                const { entryId, searchTerm } = action.payload;
                const term = (searchTerm || '').toLowerCase();
                return stateItems.map(item => {
                    if (item.id !== entryId) return item;
                    const plans = item.plans.map(p => ({
                        ...p,
                        hidden: !p.selected && !p.plan.name.toLowerCase().includes(term),
                    }));
                    return { ...item, plans };
                });
            }
            case 'ADD_SNAPSHOT_ITEM': {
                return [...stateItems, action.payload];
            }
            case 'REMOVE_SNAPSHOT_ITEM': {
                const { itemId } = action.payload;
                return stateItems.filter(item => item.id !== itemId);
            }
            case 'INVERT_SELECTION': {
                const { index } = action.payload;
                return stateItems.map((item, i) => {
                    if (i !== index) return item;
                    const plans = item.plans.map(p => ({
                        ...p,
                        selected: !p.hidden ? !p.selected : p.selected,
                    }));
                    return { ...item, plans };
                });
            }
            default:
                return stateItems;
        }
    };

    const [state, dispatch] = useReducer(reducer, []);

    useEffect(() => {
        if (init) {
            dispatch({ type: 'INIT_FROM_STATE', payload: rootState });
        }
    }, [init, rootState]);

    const togglePlanSelection = useCallback((entry: ISnapshotItem, plan: ISelectablePlan) => {
        dispatch({
            type: 'TOGGLE_PLAN_SELECTION',
            payload: { entryId: entry.id, planId: plan.plan.id! },
        });
    }, []);

    const filterPlans = useCallback((entry: ISnapshotItem, searchTerm: string) => {
        dispatch({
            type: 'FILTER_PLANS',
            payload: { entryId: entry.id, searchTerm },
        });
    }, []);

    const addSnapshotItem = useCallback((item: ISnapshotItem) => {
        dispatch({ type: 'ADD_SNAPSHOT_ITEM', payload: item });
    }, []);

    const removeSnapshotItem = useCallback((itemId: string | number) => {
        dispatch({ type: 'REMOVE_SNAPSHOT_ITEM', payload: { itemId } });
    }, []);

    const invertSelection = useCallback((index: number) => {
        dispatch({ type: 'INVERT_SELECTION', payload: { index } });
    }, []);

    return {
        snapshotItems: state,
        togglePlanSelection,
        filterPlans,
        addSnapshotItem,
        removeSnapshotItem,
        invertSelection,
    };
};

// Custom hook for managing data source
const useDataSource = () => {
    const { instance } = useMsal();
    return useMemo(() => {
        const tokenSource = new MsalTokenSource(instance);
        return { instance, dataSource: new AppDataSource(tokenSource) };
    }, [instance]);
};

const PlanComparisonReport = (props: IPlanComparisonReportProps) => {
    const state = useSelector((state: RootState) => state);
    const dispatch = useDispatch();
    const { dataSource } = useDataSource();

    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const [viewModel, setViewModel] = useState<IViewModel>({
        loading: false,
        ignoreEmptyFields: false,
        showOnlyChanges: false,
    });

    const {
        snapshotItems,
        togglePlanSelection,
        filterPlans,
        addSnapshotItem,
        removeSnapshotItem,
        invertSelection,
    } = useSnapshotItemsReducer(props.isOpen, state);

    const onRenderBody: IRenderFunction<IPanelProps> = () => {
        if (!props.isOpen)
            return <></>;

        return (
            <Stack>
                <Separator />
                <Text block>
                    Use this tool to generate a comparison report of selected plans. You can compare plans across different snapshots by importing additional snapshots as needed.
                    Select the plans you want to include in the Plan Comparison Report.
                </Text>
                <Stack
                    tokens={{ childrenGap: '5px' }}
                    styles={{ root: { marginTop: '15px' } }}
                >
                    {errorMessage && (
                        <Label styles={{ root: { color: 'red' } }}>{errorMessage}</Label>
                    )}

                    <Label>Snapshots:</Label>
                    {snapshotItems.map((snapshotItem, index) => (
                        <SnapshotPanel
                            key={snapshotItem.id}
                            snapshotItem={snapshotItem}
                            index={index}
                            state={state}
                            onTogglePlanSelection={togglePlanSelection}
                            onFilterPlans={filterPlans}
                            onRemoveSnapshot={removeSnapshotItem}
                            onInvertSelection={invertSelection}
                        />
                    ))}

                    <ImportSnapshotPanel
                        items={snapshotItems}
                        viewModel={viewModel}
                        dataSource={dataSource}
                        onAddSnapshot={addSnapshotItem}
                        onSetLoading={(loading) => setViewModel(prev => ({ ...prev, loading }))}
                    />

                    <SettingsPanel
                        viewModel={viewModel}
                        onSettingsChange={setViewModel}
                    />
                </Stack>
            </Stack>
        );
    };

    const generateReport = useCallback(async () => {

        appInsights?.startTrackEvent('PlanComparisonReport.generateReport');

        try {
            dispatch(setProcessing(true));
            const excelExtractImport = await import("global/ExcelExtract");
            const { PlanComparisonReportGeneration } = excelExtractImport;

            const options: IComparisonSettings = {
                ignoreEmptyFields: viewModel.ignoreEmptyFields,
                showOnlyChanges: viewModel.showOnlyChanges,
            };

            appInsights?.trackEvent({
                name: 'PlanComparisonReport.generateReport.selectedplans',
                properties: {
                    options: {
                        ignoreEmptyFields: options.ignoreEmptyFields,
                        showOnlyChanges: options.showOnlyChanges,
                    },
                    plans: snapshotItems.map(item => (
                        {
                            snapshotId: item.id,
                            selectedPlans: item.plans.filter(p => p.selected).map(p => (
                                {
                                    id: p.plan.id,
                                    name: p.plan.name
                                })
                            ),
                        }
                    )),
                }
            });

            const comparisonData = buildComparisonData(snapshotItems, options);

            const result = await PlanComparisonReportGeneration(
                state,
                dataSource,
                comparisonData,
            );

            const fileName = `PlanComparisonReport-${new Date().getTime()}.xlsx`;
            saveAs(result.file, fileName);
        } catch (error) {
            emitCustomEvent({
                eventType: 'globalException',
                details: error
            });
        } finally {
            dispatch(setProcessing(false));
        }

        appInsights?.stopTrackEvent('PlanComparisonReport.generateReport');
    }, [dispatch, state, dataSource, snapshotItems, viewModel]);

    const hasSelectedPlans = useMemo(() =>
        snapshotItems.some(item => item.plans.some(plan => plan.selected)),
        [snapshotItems]
    );

    const handleCancel = useCallback(() => {
        setErrorMessage(null);
        props.onCancel?.();
    }, [props]);

    const onRenderFooterContent: IRenderFunction<IPanelProps> = () => (
        <div>
            <PrimaryButton
                text={viewModel.loading ? 'Processing...' : 'Continue'}
                styles={panelButtonsStyles}
                disabled={viewModel.loading || !hasSelectedPlans}
                onClick={generateReport}
            >
                {viewModel.loading && <Spinner />}
            </PrimaryButton>
            <DefaultButton
                text='Cancel'
                onClick={handleCancel}
            />
        </div>
    );

    return (
        <Panel
            isOpen={props.isOpen}
            onDismiss={props.onDismiss}
            type={PanelType.medium}
            headerText={"Plan Comparison Report"}
            onRenderFooterContent={onRenderFooterContent}
            isFooterAtBottom={true}
        >
            {onRenderBody()}
        </Panel>
    );
}


// Component definitions
const SettingsPanel = ({
    viewModel,
    onSettingsChange
}: ISettingsPanelProps) => (
    <CustomPanel
        title="Advanced Settings"
        canCollapse={true}
        defaultCollapsed={true}
    >
        <Stack tokens={{ childrenGap: '15px' }}>
            <Checkbox
                label="Ignore empty fields when comparing"
                checked={viewModel.ignoreEmptyFields}
                onChange={(_, checked) => {
                    onSettingsChange(prev => ({ ...prev, ignoreEmptyFields: checked || false }));
                }}
            />
            <Checkbox
                label="Show only changes"
                checked={viewModel.showOnlyChanges}
                onChange={(_, checked) => {
                    onSettingsChange(prev => ({ ...prev, showOnlyChanges: checked || false }));
                }}
            />
            <Checkbox
                disabled={true}
                label="Auto Map tiers (comprehensive->InNetwork)"
            />
            <Checkbox
                disabled={true}
                label="Single Sheet with segment breaks"
            />
        </Stack>
    </CustomPanel>
);

const TopBarComponent = ({ onSearch, onClear }: ITopBarComponentProps) => {
    const defaultQueryMenuItems = useMemo((): IContextualMenuItem[] => [
        {
            key: 'default',
            text: 'Default Query',
        },
    ], []);

    return (
        <Stack horizontal verticalAlign='center' horizontalAlign='space-between'>
            <Stack.Item>
                <LinkMenu
                    menuItems={defaultQueryMenuItems}
                    selectedKey={"default"}
                />
            </Stack.Item>
            <Stack.Item>
                {/* Spacer */}
            </Stack.Item>
            <Stack.Item>
                <Stack horizontal horizontalAlign='end' verticalAlign='center'>
                    <Stack.Item>
                        <SearchBox
                            placeholder="Quick Search"
                            styles={{
                                root: {
                                    transform: 'scale(0.7,0.7)',
                                    transformOrigin: 'right',
                                    backgroundColor: 'transparent',
                                    width: '140px'
                                }
                            }}
                            onSearch={onSearch}
                            onClear={onClear}
                        />
                    </Stack.Item>
                </Stack>
            </Stack.Item>
        </Stack>
    );
};


const SnapshotPanel = ({
    snapshotItem,
    index,
    state,
    onTogglePlanSelection,
    onFilterPlans,
    onRemoveSnapshot,
    onInvertSelection,
}: ISnapshotPanelProps) => {
    const menuItems = useMemo((): IContextualMenuItem[] => {
        const items: IContextualMenuItem[] = [
            {
                key: 'invertSelection',
                name: '',
                icon: 'ClearSelection',
                onClick: (event) => {
                    event?.stopPropagation();
                    onInvertSelection(index);
                }
            }
        ];

        // if (index > 0) {
        items.push({
            key: 'delete',
            name: '',
            icon: 'Delete',
            onClick: (event) => {
                event?.stopPropagation();
                onRemoveSnapshot(snapshotItem.id);
            }
        });
        // }

        return items;
    }, [index, onInvertSelection, onRemoveSnapshot, snapshotItem.id]);

    return (
        <CustomPanel
            title={snapshotItem.snapshot.bcbs_name || 'Current Working Snapshot'}
            canCollapse={true}
            defaultCollapsed={false}
            key={snapshotItem.id}
            items={menuItems}
        >
            <TopBarComponent
                onSearch={(value) => onFilterPlans(snapshotItem, value.toLowerCase())}
                onClear={() => onFilterPlans(snapshotItem, '')}
            />
            <Stack
                horizontal
                wrap
                verticalFill
                tokens={{ childrenGap: 15 }}
                horizontalAlign="start"
                style={{ padding: '3px', marginBottom: '3px' }}
            >
                {snapshotItem.plans.map(eachPlan => (
                    <PlanCard
                        key={eachPlan.plan.id}
                        plan={eachPlan.plan}
                        selected={eachPlan.selected}
                        disabled={eachPlan.plan.deactivated || eachPlan.hidden}
                        onClick={() => onTogglePlanSelection(snapshotItem, eachPlan)}
                        state={state}
                    />
                ))}
            </Stack>
        </CustomPanel>
    );
};


const ImportSnapshotPanel = ({
    items,
    viewModel,
    dataSource,
    onAddSnapshot,
    onSetLoading,
}: IImportSnapshotPanelProps) => {

    const [counter, setCounter] = useState(0);

    const handleSnapshotPickAsync = useCallback(
        async (value: any) => {
            setCounter(prev => prev + 1); // Force remount
            if (items.some(item => item.id === value?.id)) return;

            try {
                onSetLoading(true);

                const snapshot = await dataSource.getGroupConfigurationSnapshot(value?.id!);
                const jsonResponse = await dataSource.downloadGroupConfigurationSnapshot(snapshot!);
                const json = jsonResponse?.json!;

                const snapshotItem = createSnapshotItemFromSnapshot(snapshot, json);
                onAddSnapshot(snapshotItem);

                appInsights?.trackEvent({
                    name: 'PlanComparisonReport.ImportSnapshot',
                    properties: {
                        snapshotId: value?.id,
                        snapshotName: snapshot?.bcbs_name
                    }
                });

            } catch (error) {
                emitCustomEvent({
                    eventType: 'globalException',
                    details: error
                });
            } finally {
                onSetLoading(false);

            }
        },
        [items, dataSource, onAddSnapshot, onSetLoading]
    );

    return (
        <CustomPanel
            title="Import Additional Snapshot"
            canCollapse={false}
            defaultCollapsed={false}
        >
            <SnapshotLookup
                key={counter}
                searchOnMount={false}
                onPick={handleSnapshotPickAsync}
            />
            {viewModel.loading && (
                <ProgressIndicator label="Processing..." />
            )}
        </CustomPanel>
    );
};

export default PlanComparisonReport;